// src/pages/EditCropAssessment.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const EditCropAssessment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    // Relevant Crops for Wind Machines
    windCrop: "",
    windAnnualProductionVolume: "",
    windGlobalProductionPercentage: "",
    windAcreageSize: "",
    windCropReturns: "",
    // Relevant Crops for Tree Shakers
    treeCrop: "",
    treeAnnualProductionVolume: "",
    treeGlobalProductionPercentage: "",
    treeAcreageSize: "",
    treeCropReturns: ""
  });

  useEffect(() => {
    fetch(`/api/countries/${id}/crop-assessment`)
      .then((res) => res.json())
      .then((data) =>
        setFormData({
          windCrop: data.wind_crop || "",
          windAnnualProductionVolume: data.wind_annual_production_volume || "",
          windGlobalProductionPercentage: data.wind_global_production_percentage || "",
          windAcreageSize: data.wind_acreage_size || "",
          windCropReturns: data.wind_crop_returns || "",
          treeCrop: data.tree_crop || "",
          treeAnnualProductionVolume: data.tree_annual_production_volume || "",
          treeGlobalProductionPercentage: data.tree_global_production_percentage || "",
          treeAcreageSize: data.tree_acreage_size || "",
          treeCropReturns: data.tree_crop_returns || ""
        })
      )
      .catch((err) => console.error("Error fetching crop assessment", err));
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetch(`/api/countries/${id}/crop-assessment`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData)
    })
      .then(() => navigate(`/country/${id}`))
      .catch((err) => console.error("Error updating crop assessment", err));
  };

  const containerStyle: React.CSSProperties = {
    backgroundColor: "#F0F5F0",
    minHeight: "100vh",
    padding: "20px"
  };
  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    maxWidth: "800px",
    margin: "30px auto",
    padding: "30px"
  };
  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    borderBottom: "2px solid #e41e26",
    paddingBottom: "10px",
    marginBottom: "20px",
    fontSize: "24px"
  };
  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: "5px",
    fontWeight: "bold"
  };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px",
    marginBottom: "10px",
    borderRadius: "4px",
    border: "1px solid #ccc"
  };
  const buttonStyle: React.CSSProperties = {
    padding: "5px 10px",
    backgroundColor: "#e41e26",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginRight: "10px"
  };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Crop Assessment</h1>
        <form onSubmit={handleSubmit}>
          <h3
            style={{
              color: "#e41e26",
              borderBottom: "1px solid #e41e26",
              paddingBottom: "5px",
              marginTop: "15px"
            }}
          >
            Relevant Crops for Wind Machines
          </h3>

          <label style={labelStyle}>Crop:</label>
          <input
            type="text"
            name="windCrop"
            style={inputStyle}
            value={formData.windCrop}
            onChange={handleChange}
            placeholder="Enter crop name"
          />

          <label style={labelStyle}>
            Annual Production Volume (metric tons)
          </label>
          <input
            type="number"
            name="windAnnualProductionVolume"
            style={inputStyle}
            value={formData.windAnnualProductionVolume}
            onChange={handleChange}
            placeholder="Numeric value, e.g. 1000"
            inputMode="decimal"
            step="any"
          />

          <label style={labelStyle}>
            Global Production Percentage (%)
          </label>
          <input
            type="number"
            name="windGlobalProductionPercentage"
            style={inputStyle}
            value={formData.windGlobalProductionPercentage}
            onChange={handleChange}
            placeholder="Percentage, e.g. 12.5"
            inputMode="decimal"
            step="0.01"
            min="0"
            max="100"
          />

          <label style={labelStyle}>Acreage</label>
          <input
            type="number"
            name="windAcreageSize"
            style={inputStyle}
            value={formData.windAcreageSize}
            onChange={handleChange}
            placeholder="Numeric value, e.g. 2500"
            inputMode="decimal"
            step="any"
          />

          <label style={labelStyle}>Crop Returns (%)</label>
          <input
            type="number"
            name="windCropReturns"
            style={inputStyle}
            value={formData.windCropReturns}
            onChange={handleChange}
            placeholder="Percentage, e.g. 5.25"
            inputMode="decimal"
            step="0.01"
            min="0"
            max="100"
          />

          <h3
            style={{
              color: "#e41e26",
              borderBottom: "1px solid #e41e26",
              paddingBottom: "5px",
              marginTop: "15px"
            }}
          >
            Relevant Crops for Tree Shakers
          </h3>

          <label style={labelStyle}>Crop:</label>
          <input
            type="text"
            name="treeCrop"
            style={inputStyle}
            value={formData.treeCrop}
            onChange={handleChange}
            placeholder="Enter crop name"
          />

          <label style={labelStyle}>
            Annual Production Volume (metric tons)
          </label>
          <input
            type="number"
            name="treeAnnualProductionVolume"
            style={inputStyle}
            value={formData.treeAnnualProductionVolume}
            onChange={handleChange}
            placeholder="Numeric value, e.g. 1000"
            inputMode="decimal"
            step="any"
          />

          <label style={labelStyle}>
            Global Production Percentage (%)
          </label>
          <input
            type="number"
            name="treeGlobalProductionPercentage"
            style={inputStyle}
            value={formData.treeGlobalProductionPercentage}
            onChange={handleChange}
            placeholder="Percentage, e.g. 12.5"
            inputMode="decimal"
            step="0.01"
            min="0"
            max="100"
          />

          <label style={labelStyle}>Acreage</label>
          <input
            type="number"
            name="treeAcreageSize"
            style={inputStyle}
            value={formData.treeAcreageSize}
            onChange={handleChange}
            placeholder="Numeric value, e.g. 2500"
            inputMode="decimal"
            step="any"
          />

          <label style={labelStyle}>Crop Returns (%)</label>
          <input
            type="number"
            name="treeCropReturns"
            style={inputStyle}
            value={formData.treeCropReturns}
            onChange={handleChange}
            placeholder="Percentage, e.g. 5.25"
            inputMode="decimal"
            step="0.01"
            min="0"
            max="100"
          />

          <div style={{ marginTop: "20px" }}>
            <button type="submit" style={buttonStyle}>
              Save
            </button>
            <button
              type="button"
              style={buttonStyle}
              onClick={() => navigate(`/country/${id}`)}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCropAssessment;
